import React from 'react';
import type { MediaOverlays } from '../types';
import { fontById, strokePath, TOOL_STYLE } from '../utils/overlays';

interface OverlayLayerProps {
  overlays?: MediaOverlays | null;
  // 'cover' matches media shown with object-cover (the overlay box fills and crops like the media does),
  // 'contain' matches object-contain.
  fit?: 'cover' | 'contain';
  className?: string;
}

// Draws the text + hand-drawn edits made in the MediaEditor on top of a photo/video. The inner box has the media's own
// aspect ratio and is sized with container-query units so it lines up with the media under the same fit rule.
const OverlayLayer: React.FC<OverlayLayerProps> = ({ overlays, fit = 'cover', className = '' }) => {
  if (!overlays || (!overlays.texts?.length && !overlays.strokes?.length)) return null;
  const a = overlays.aspect > 0 ? overlays.aspect : 1;
  const h = 1000 / a;
  const width = fit === 'cover' ? `max(100cqw, calc(100cqh * ${a}))` : `min(100cqw, calc(100cqh * ${a}))`;

  return (
    <div className={`pointer-events-none absolute inset-0 z-[2] overflow-hidden ${className}`.trim()} style={{ containerType: 'size' }} aria-hidden="true">
      <div
        className="absolute left-1/2 top-1/2"
        style={{ width, aspectRatio: String(a), transform: 'translate(-50%, -50%)', containerType: 'inline-size' }}
      >
        <svg viewBox={`0 0 1000 ${h}`} className="absolute inset-0 h-full w-full" preserveAspectRatio="none">
          {overlays.strokes.map((s) => {
            const st = TOOL_STYLE[s.tool] || TOOL_STYLE.pen;
            return (
              <path
                key={s.id}
                d={strokePath(s.points, h)}
                fill="none"
                stroke={s.color}
                strokeWidth={s.width * 1000 * st.mult}
                strokeOpacity={st.opacity}
                strokeLinecap={st.cap}
                strokeLinejoin="round"
              />
            );
          })}
        </svg>
        {(overlays.texts || []).map((t) => {
          if (!t.text.trim()) return null;
          const f = fontById(t.font);
          return (
            <div
              key={t.id}
              className="absolute whitespace-pre-wrap text-center leading-tight"
              style={{
                left: `${t.x * 100}%`,
                top: `${t.y * 100}%`,
                transform: 'translate(-50%, -50%)',
                maxWidth: '90%',
                fontFamily: f.family,
                fontWeight: f.weight,
                fontSize: `${t.size * 100}cqw`,
                color: t.color,
                textShadow: t.box ? 'none' : '0 1px 6px rgba(0,0,0,0.45)',
                background: t.box ? 'rgba(0,0,0,0.55)' : 'transparent',
                padding: t.box ? '0.18em 0.4em' : 0,
                borderRadius: t.box ? '0.3em' : 0,
              }}
            >
              {t.text}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OverlayLayer;
